import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { PatientProfileForms } from '@bepatient/patient-profile-store';
import { TranslateService } from '@ngx-translate/core';
import { lengthValidator } from '../../dynamic-form-v3/validators/dynamic-form-length.validator';
import {
  ConstraintTypesV3,
  QuestionV3ControlTypes,
  QuestionsV3,
  ConditionalRuleTypes,
  ConditionalRuleType
} from '../classes/enum';
import {
  DynamicFormInputQuestionV3,
  QuestionOptionV3
} from '../models/dynamic-form-v3.models';
import { emailValidator } from '../validators/dynamic-form-email.validator';
import {
  allowedExtensionValidator,
  maxSizeValidator
} from '../validators/dynamic-form-photo.validator';
import { createRangeValidator } from '../validators/dynamic-form-range.validator';
import { RequiredGroupValidator } from '../validators/dynamic-form-requiredGroup.validator';

interface ContraintFromMobi {
  required?: true;
  max?: number;
  min?: number;
  max_length?: number;
  min_length?: number;
  length?: number;
  message: string;
  pattern?: boolean;
  email?: boolean;
  conditional?: boolean;
  limit_condition?: boolean;
  max_size?: number;
  allowed_extensions?: string[];
}

@Injectable()
export class DynamicFormQuestionParserServiceV3 {
  private translate = inject(TranslateService);

  /**
   * Parsing form data to be sent in api request.
   * @param values
   * @param questions
   * @returns
   */
  parseFormValuesForMobi(values, questions): any {
    return questions
      .filter(
        question => question.controlType !== QuestionV3ControlTypes.SEMANTIC
      )
      .reduce((formValue, question) => {
        // README: value of a photo should be an array of array : [['img.jpg', 'image/jpeg', values[question.key]]]
        // Removed Array of array from SMARTAPP-681 : values[question.key]
        if (question.controlType === QuestionV3ControlTypes.PHOTO) {
          return {
            ...formValue,
            [question.key]: !values[question.key] ? '' : values[question.key]
          };
        }

        /*
          For checkbox fields etc....
          Angular form sends an array of Booleans
          we need to modify this Array
          to send only the value to MOBI
          */

        if (Array.isArray(values[question.key])) {
          const parsedArrayOfAnswers = values[question.key]
            .map((value, index) => {
              if (value) {
                return question.controlType ===
                  QuestionV3ControlTypes.MULTI_DROPDOWN ||
                  question.controlType === QuestionV3ControlTypes.DROPDOWN
                  ? value
                  : question.options[index].value;
              }
            })
            .filter(value => value);

          return {
            ...formValue,
            [question.key]: parsedArrayOfAnswers
          };
        }

        /*
          For decimal fields, replace comma with
          dot before sending the value to MOBI
        */

        if (
          question.controlType === QuestionV3ControlTypes.TEXT &&
          question.type === 'decimal'
        ) {
          return {
            ...formValue,
            [question.key]: !values[question.key]
              ? ''
              : values[question.key].toString().replace(',', '.')
          };
        }

        /**
         * Datetime format change to backend format
         */
        if (
          question.controlType === QuestionV3ControlTypes.DATETIME &&
          !!values[question.key]
        ) {
          const mobiFormat = question.mobiDateFormat.toLowerCase();
          const dateValue = values[question.key] ? values[question.key] : '';

          // The datetime component now handles all formatting using question.format
          // Just pass the value through without additional processing
          return {
            ...formValue,
            [question.key]: dateValue
          };
        }

        /*
          all other values including **number** have to be string
        */

        return {
          ...formValue,
          [question.key]:
            !values[question.key] && values[question.key] !== 0
              ? ''
              : values[question.key].toString()
        };
      }, {});
  }

  public parseQuestions(
    questions,
    responses?,
    formIsReadOnly?: boolean
  ): DynamicFormInputQuestionV3[] {
    const parsedQuestions = questions.map(question => {
      const { name: key, label, logic, page, type, dualKnobs, step } = question;
      const options = this.getRadioOptions(question);
      const conditionalRules = this.parseConditionalRules(question); //this.addConditionalRulesToQuestion(question);
      const displayFormat = this.getDateDisplayFormat(question.format);
      const min = this.getMinMaxValue(question, 'min');
      const max = this.getMinMaxValue(question, 'max');
      const maxSize = this.getMaxFileSize(question);
      const minIndicative = question.min_value_indicative;
      const maxIndicative = question.max_value_indicative;
      const mobiDateFormat = question.format;
      const allowFutureDate = question.allow_future_date;
      const pickerFormat = displayFormat;
      const constraintsAndValidator =
        this.parseConstraintsFromMobi(question) || {};
      const customClasses = !responses
        ? ['']
        : this.addCustomCSSClasses(question, responses);

      const questionFields =
        question.type === 'fields-block' ? question.fields : [];
      const css = question.css;
      const defaultValue = question.default_value || void 0;
      const hasCondition =
        conditionalRules && conditionalRules.length > 0 ? true : false;
      const display = this.getQuestionDisplayValue(question, conditionalRules);
      const showTicks = question.show_graduation || false;
      const showSliderValue =
        question.show_slider_value == undefined
          ? true
          : question.show_slider_value;

      return {
        ...constraintsAndValidator,
        key,
        label,
        css,
        logic,
        displayFormat,
        allowFutureDate,
        mobiDateFormat,
        pickerFormat,
        dualKnobs,
        min,
        max,
        maxSize,
        minIndicative,
        maxIndicative,
        options,
        customClasses,
        display,
        conditionalRules,
        page,
        format: question.format,
        step: parseFloat(step),
        src: question.href || undefined,
        type: this.parseType(type, question),
        useKey: question.use_key || undefined,
        startLabel: question.min_label || undefined,
        endLabel: question.max_label || undefined,
        showTicks: showTicks,
        showSliderValue: showSliderValue,
        displayValue: question.display_value || undefined,
        useValidatedSlider: question.use_validated_slider || false,
        required: parseInt(question.required, 10) === 1,
        readonly: formIsReadOnly,
        placeholder: this.getPlaceholder(question),
        text: question.text || undefined,
        useLibrary: question.use_library,
        repetitions: questionFields,
        defaultValue,
        condition: question.display_condition,
        compulsoryCondition: question.compulsory_condition,
        limitCondition: {
          condition: question.boundary_condition,
          message: question.boundary_message
        },
        isHeadline: question.is_headline,
        hasCondition,
        video: {
          videoType: question.video_type || undefined,
          url: question.url || undefined
        },
        target: question?.target || undefined
      };
    });
    return parsedQuestions;
  }

  // public toggleDisplayForConditionnalQuestions(
  //   question: QuestionsV3,
  //   formValues
  // ): boolean {
  //   const parsedFormValues = this.parseArrayOfBooleanInFormValues(formValues);
  //   if (!question.conditionalRules.isConditionnal) {
  //     return true;
  //   }
  //   if (question.conditionalRules.hasMultipleParentQuestions) {
  //     return this.areParentQuestionsValid(question, parsedFormValues);
  //   } else {
  //     return this.isParentQuestionValid(question, parsedFormValues);
  //   }
  // }

  /**
   * Check FormGroup status for currentPage
   * @param pages
   * @param currentPage
   * @returns
   */
  public checkPageValidation(pages, currentPage) {
    // Display popup message if required field is invalid or empty
    let goToNextPage = false;
    // if status is valid gotoNextPage true
    if (pages && currentPage >= 0) {
      const cPage = pages[currentPage];
      cPage && cPage.formGroup.status === 'INVALID'
        ? (goToNextPage = false)
        : (goToNextPage = true);
      return goToNextPage;
    }
    return true;
  }

  public patientProfileParseSelectedActivity(activity): any {
    return {
      date: activity.mtime,
      answersUuid: activity.uuid
    };
  }

  public patientProfileFormsParser(
    forms: Array<any>,
    isProForm: boolean
  ): PatientProfileForms[] {
    return forms.map(form => {
      const name = (form['meta'] && form['meta']['title']) || void 0;
      const date =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['mtime']) ||
        void 0;

      const toBeChecked =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['to_be_checked']) ||
        void 0;

      const uuid = form['uuid'] || void 0;
      const haveLastActivity =
        (form['meta'] && form['meta']['last_activity']) || void 0;

      const status =
        (form['meta'] &&
          form['meta']['last_activity'] &&
          form['meta']['last_activity']['status']) ||
        void 0;

      return {
        name,
        date,
        toBeChecked,
        uuid,
        haveLastActivity,
        isProForm,
        status
      };
    });
  }

  public patientProfileParseResponses(questions, responses): any {
    return responses.reduce((acc, response) => {
      const { uuid, value, to_be_checked: toBeChecked } = response;
      const currentQuestion = questions.find(question =>
        question.type !== 'fields-block'
          ? question.name === uuid
          : question.fields.find(rep =>
              rep.find(field => field.field_key === uuid)
            )
      );
      const { type } = currentQuestion;
      switch (type) {
        case 'checkbox':
        case 'toggle':
          const options = this.transformOptionsObjectToArray(
            currentQuestion.options
          );
          acc[uuid] = options.map(question => value.includes(question.name));
          break;

        case 'boolean':
          /*
              For this type we need to transform the boolean value sent by the
              API to '1' or '0'
              '0' or '1' are understood by ion-group-radio. So the proper
              radio value will be checked in the interface
              Note it must only apply to 'boolean' type. We do not parse 'radio' type
          */
          acc[uuid] = value ? '1' : '0';
          break;

        default:
          acc[uuid] = value;
          break;
      }
      return acc;
    }, {});
  }

  public isFormReadonly(links: any): boolean {
    // TODO : TS2339 Property 'includes' does not exist on type '[string]'
    return Object.keys(links).includes('submit') ? false : true;
  }

  // Method to receive question and form values to evaluate expression
  // For now display & compulsory only!!
  public evaluateQuestionExpression(question, formValues): any {
    const parsedFormValues = this.parseArrayOfBooleanInFormValues(formValues);
    return this.evaluateParentQuestionsValid(question, parsedFormValues);
  }

  // SMARTAPP: Display conditional question parser
  // public checkDisplayForConditionalQuestions(
  //   question: QuestionsV3,
  //   formValues
  // ): boolean {
  //   const parsedFormValues = this.parseArrayOfBooleanInFormValues(formValues);
  //   if (parsedFormValues) {
  //     return this.evaluateParentQuestionsValid(question, parsedFormValues);
  //   }
  // }

  // SMARTAPP: Compulsory conditional question parser
  // public checkCompulsoryConditionalQuestions(
  //   question: QuestionsV3,
  //   formValues,
  //   doCompulsoryCondition?: boolean
  // ): boolean {
  //   const parsedFormValues = this.parseArrayOfBooleanInFormValues(formValues);
  //   if (question.compulsoryCondition && doCompulsoryCondition) {
  //     return this.evaluateParentQuestionsValid(
  //       question,
  //       parsedFormValues,
  //       doCompulsoryCondition,
  //       false
  //     );
  //   } else {
  //     return false;
  //   }
  // }

  // public checkLimitConditionalQuestions(
  //   question: QuestionsV3,
  //   formValues,
  //   doLimitCondition?: boolean
  // ): boolean {
  //   const parsedFormValues = this.parseArrayOfBooleanInFormValues(formValues);
  //   if (question.limitCondition && doLimitCondition) {
  //     return this.evaluateParentQuestionsValid(
  //       question,
  //       parsedFormValues,
  //       false,
  //       doLimitCondition
  //     );
  //   } else {
  //     return false;
  //   }
  // }

  // Returns a object with on valid value
  // they must be !== null or undefined or empty string or an array of those
  // elements
  private objWithoutFaultyValue(obj: any): any {
    const faultyValues = [null, undefined, ''];
    const isArray = arr => Array.isArray(arr),
      // TODO : TS2339 Property 'includes' does not exist on type '[string]'
      isOnlyNull = arr => arr.every(val => faultyValues.includes(val)),
      isObject = _obj => typeof _obj === 'object';

    return (
      Object.keys(obj)
        // TODO : TS2339 Property 'includes' does not exist on type '[string]'
        .filter(key => !faultyValues.includes(obj[key]))
        .reduce((acc, key) => {
          if (isArray(obj[key])) {
            if (!isOnlyNull(obj[key])) {
              acc[key] = obj[key];
            }
          } else if (isObject(obj[key])) {
            // TODO : TS2339 Property 'values' does not exist on type 'ObjectConstructor'
            if (!isOnlyNull(Object.values(obj[key]))) {
              acc[key] = Object.values(obj[key]);
            }
          } else {
            acc[key] = obj[key];
          }
          return acc;
        }, {})
    );
  }

  private getMaxFileSize(question): string | number {
    const checkMaxFileSize =
      question &&
      question.constraints &&
      question.constraints.find(constraint => Boolean(constraint['max_size']));

    return Boolean(checkMaxFileSize) ? checkMaxFileSize['max_size'] : 10;
  }

  private getMinMaxValue(question, type: 'max' | 'min'): string | number {
    const constraintObject =
      question &&
      question.constraints &&
      question.constraints.find(constraint => Boolean(constraint[type]));

    return Boolean(constraintObject) ? constraintObject[type] : undefined;
  }

  private getRadioOptions(question: any): QuestionOptionV3[] {
    return question.options
      ? Object.keys(question.options).map(_key => {
          return {
            key: question.options[_key].label,
            value: question.options[_key].value,
            imageSrc: question.options[_key].image_src
          };
        })
      : [];
  }

  // Build dynamic placeholder key like PLACEHOLDERS.DATETIME.DD_MM_YYYY_HH_MM_AM_PM
  private buildDatetimePlaceholderKey(fmtRaw: string): string {
    const raw = (fmtRaw || '').trim();
    const lower = raw.toLowerCase();

    // Detect 12h vs 24h using ORIGINAL case (so we don't lose hh vs HH)
    const is12h = /hh/.test(raw) && !/HH/.test(raw);
    const hasTime = /h{1,2}:?m{1,2}/i.test(raw) || /hh|HH/.test(raw);

    // Identify date shape
    const startsWithDD = lower.startsWith('dd');
    const startsWithYYYY = lower.startsWith('yyyy');
    const hasYYYY = lower.includes('yyyy');
    const hasDD = lower.includes('dd');
    const hasMM = /(^|[^h])mm([^a-z]|$)/.test(lower); // mm not from hh:mm minutes context

    let dateKey = '';
    if (hasYYYY && hasMM && hasDD) {
      dateKey = startsWithDD
        ? 'DD_MM_YYYY'
        : startsWithYYYY
        ? 'YYYY_MM_DD'
        : 'DD_MM_YYYY';
    } else if (hasMM && hasYYYY && !hasDD) {
      // Month-Year only
      dateKey = 'MM_YYYY';
    } else if (hasYYYY && !hasMM && !hasDD) {
      dateKey = 'YYYY';
    }

    let timeKey = '';
    if (hasTime) {
      timeKey = is12h ? 'HH_MM_AM_PM' : 'HH_MM';
    }

    // Combine
    let result = '';
    if (dateKey && timeKey) result = `${dateKey}_${timeKey}`;
    else if (dateKey) result = dateKey;
    else if (timeKey) result = timeKey;
    else result = 'HH_MM'; // Fallback

    return result;
  }

  /**
   * SMARTAPP-312:
   * Returns the date format which meets the condition specified.
   * @param format A string that is used for condition that returns date format.
   * @param translate OPTIONAL | Boolean, if provided it will return translated date format, used for DATETIME placeholder.
   */
  private getDateDisplayFormat(
    expectedDateFormat: string,
    translate?: boolean
  ): string {
    const key = this.buildDatetimePlaceholderKey(expectedDateFormat);
    if (translate) {
      return `PLACEHOLDERS.DATETIME.${key}`;
    }

    // Build human-readable mask from the dynamic key
    // Examples:
    //  - DD_MM_YYYY            -> DD/MM/YYYY
    //  - YYYY_MM_DD_HH_MM      -> YYYY/MM/DD - HH:mm
    //  - DD_MM_YYYY_HH_MM_AM_PM-> DD/MM/YYYY - hh:mm a
    //  - HH_MM_AM_PM           -> hh:mm a
    //  - HH_MM                  -> HH:mm
    let datePart = '';
    if (key.startsWith('DD_MM_YYYY')) {
      datePart = 'DD/MM/YYYY';
    } else if (key.startsWith('YYYY_MM_DD')) {
      datePart = 'YYYY/MM/DD';
    } else if (key.startsWith('MM_YYYY')) {
      datePart = 'MM/YYYY';
    } else if (key === 'YYYY' || key.startsWith('YYYY_')) {
      datePart = 'YYYY';
    }

    let timePart = '';
    if (key.endsWith('HH_MM_AM_PM')) {
      timePart = 'hh:mm a';
    } else if (key.endsWith('HH_MM')) {
      timePart = 'HH:mm';
    }

    if (datePart && timePart) return `${datePart} - ${timePart}`;
    if (datePart) return datePart;
    if (timePart) return timePart;

    // Fallback
    return 'HH:mm';
  }

  private getPlaceholder(question): string {
    switch (question.type) {
      case 'integer':
        return 'PLACEHOLDERS.INTEGER';
      case 'text':
        return 'PLACEHOLDERS.TEXT';

      case 'number':
        return 'PLACEHOLDERS.NUMBER';

      case 'email':
        return 'PLACEHOLDERS.EMAIL';

      case 'textarea':
        return 'PLACEHOLDERS.TEXTAREA';

      case 'select':
        return 'PLACEHOLDERS.SELECT';

      case 'social_security':
        return question.use_key
          ? 'PLACEHOLDERS.SOCIAL_SECURITY_WITH_KEY'
          : 'PLACEHOLDERS.SOCIAL_SECURITY';

      case 'datetime':
      case 'date':
        const format = this.getDateDisplayFormat(question.format, true);
        return format;

      case 'time':
        const timeFormat = this.getDateDisplayFormat(question.format, true);
        return timeFormat;

      default:
        return void 0;
    }
  }

  private parseConstraintsFromMobi(question: any) {
    let constraints: ContraintFromMobi[];
    let validators = [];

    constraints = !!question.constraints ? question.constraints : [];

    // // Decimal & Integer question have pattern constraints
    // // Backend does not provide this constraints
    if (
      question.type === 'decimal' ||
      question.type === 'number' ||
      question.type === 'integer'
    ) {
      if (!constraints.find(c => c.pattern)) {
        constraints.push({
          pattern: true,
          message: this.translate.instant('FORM_V2.ERROR.INVALID_VALUE')
        });
      }
    }

    // Email question has email constraints
    if (question.type === 'email') {
      if (!constraints.find(c => c.email)) {
        constraints.push({
          email: true,
          message: this.translate.instant('FORM_V2.ERROR.INVALID_VALUE')
        });
      }
    }

    // Photo question has file size constraints
    if (question.type === 'photo') {
      if (!constraints.find(c => c.max_size)) {
        constraints.push({
          max_size: 10,
          message: this.translate.instant('FORM_V2.ERROR.FILESIZE', {
            limit: 10
          })
        });
      }

      if (!constraints.find(c => c.allowed_extensions)) {
        constraints.push({
          allowed_extensions: ['image/png', 'image/jpg', 'image/jpeg'],
          message: this.translate.instant('FORM_V2.ERROR.INVALID_EXTENSION')
        });
      }
    }

    // Setting constraints for Limit condition
    if (question.boundary_condition) {
      if (!constraints.find(c => c.limit_condition)) {
        constraints.push({
          limit_condition: true,
          message: question.boundary_message
        });
      }
    }

    // Adding conditional flag for required constraints
    // if (question.compulsory_condition) {
    //   const requiredIndex = question.constraints.findIndex(
    //     constraint => constraint.required !== undefined
    //   );
    // question.constraints[requiredIndex].conditional = true;
    // constraints.push({
    //   required: true,
    //   message: constraints['message']
    // })
    // if (!constraints.find(c => c.required)) {
    //   constraints.push({
    //     required: true,
    //     message: c
    //   });
    // }
    // }

    return {
      constraints: constraints
        .map(constraint => {
          if (constraint.required) {
            question.type === 'checkbox' || question.type === 'toggle'
              ? validators.push(RequiredGroupValidator())
              : validators.push(Validators.required);
            return {
              type: ConstraintTypesV3.REQUIRED,
              errorMessage: constraint['message']
            };
          }
          if (constraint.min >= 0 && constraint.max) {
            validators.push(
              createRangeValidator(constraint.min, constraint.max)
            );
            return {
              type: ConstraintTypesV3.RANGE,
              max: constraint.max,
              min: constraint.min,
              errorMessage: constraint['message']
            };
          }
          if (constraint.email) {
            validators.push(emailValidator());
            return {
              type: ConstraintTypesV3.EMAIL,
              errorMessage: this.translate.instant(
                'FORM_V2.ERROR.INVALID_VALUE'
              )
            };
          }
          if (constraint.pattern) {
            return {
              type: ConstraintTypesV3.PATTERN,
              errorMessage: this.translate.instant(
                'FORM_V2.ERROR.INVALID_VALUE'
              )
            };
          }
          if (constraint.min >= 0) {
            validators.push(Validators.min(constraint.min));
            return {
              type: ConstraintTypesV3.MIN,
              min: constraint.min,
              errorMessage: constraint['message']
            };
          }
          if (constraint.max) {
            validators.push(Validators.max(constraint.max));
            return {
              type: ConstraintTypesV3.MAX,
              max: constraint.max,
              errorMessage: constraint['message']
            };
          }
          if (
            constraint.max_length &&
            question.type !== 'date' &&
            question.type !== 'datetime'
          ) {
            validators.push(Validators.maxLength(constraint.max_length));
            return {
              type: ConstraintTypesV3.MAXLENGTH,
              max: constraint.max_length,
              errorMessage: constraint['message']
            };
          }
          // TODO: New Added
          if (constraint.min_length) {
            validators.push(Validators.minLength(constraint.min_length));
            return {
              type: ConstraintTypesV3.MINLENGTH,
              min: constraint.min_length,
              errorMessage: constraint['message']
            };
          }

          if (constraint.length) {
            validators.push(lengthValidator(constraint.length, true));
            return {
              type: ConstraintTypesV3.LENGTH,
              len: constraint.length,
              errorMessage: constraint['message']
            };
          }

          // TODO: LIMIT CONDITION
          if (constraint.limit_condition) {
            return {
              type: ConstraintTypesV3.LIMIT_CONDITION,
              errorMessage: constraint['message']
            };
          }
          if (constraint.max_size) {
            validators.push(maxSizeValidator(constraint.max_size));
            return {
              type: ConstraintTypesV3.MAXSIZE,
              maxSize: constraint.max_size,
              errorMessage: constraint['message']
            };
          }

          if (constraint.allowed_extensions) {
            validators.push(
              allowedExtensionValidator(constraint.allowed_extensions)
            );
            return {
              type: ConstraintTypesV3.ALLOWED_EXTENSION,
              allowedExtension: constraint.allowed_extensions,
              errorMessage: constraint['message']
            };
          }
        })
        .filter(constraint => !!constraint),
      validators: validators
    };
  }

  private getQuestionDisplayValue(question, conditionalRules): boolean {
    if (question && conditionalRules && conditionalRules.length > 0) {
      if (
        conditionalRules.find(
          rule => rule.type == ConditionalRuleTypes.DISPLAY_CONDITION
        )
      ) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  // SMARTAPP: Evaluate parent questions
  private evaluateParentQuestionsValid(
    conditionnalQuestion: QuestionsV3,
    formValues: any
    // doCompulsoryCondition?: boolean,
    // doLimitCondition?: boolean
  ): any {
    const object = formValues;
    // let decision: boolean;

    /**
     * @ignore this comment
     * This is just a draft analysis what to do not to do
     * Use promise for always required condition test
     * Use switch case to get exact conditional case and check condition
     */

    /**
     * @ignore this comment
     * Map question.conditionalRules
     * Check which conditions it has
     *
     * After get decision -> SET DECISION value inside conditionalRules -> result (TODO: create new variable inside conditionalRuleType interface)
     *
     * If display_condition -> get decision
     * If compulsory_condition -> get decision
     *  - 1. if display_condition?.decision = true -> get decision
     *  - 2. if !display_condition (already question.display = true) -> get decision
     *
     * If limit_condition -> get decision
     * - 1. If display_condition?.decision = true && compulsory_condition?.decision = true -> get decision
     * - 2. If !display_condition (already display = true) -> get decision
     * - 3. If compulsory_condition
     */

    /**
    * * @ignore this comment
    * Below code was to return void 0 if there is no expression found for any condition
    * New parseConditionalRules always send condition if there is expression
    * Not required below code
    *

    // if (
    //   (doCompulsoryCondition && !conditionnalQuestion.compulsoryCondition) ||
    //   (doLimitCondition && !conditionnalQuestion.limitCondition.condition) ||
    //   (!doCompulsoryCondition &&
    //     !doLimitCondition &&
    //     !conditionnalQuestion.condition)
    // ) {
    //   return void 0;
    // }

    /**
     * @ignore this comment
     * let display = true;
     * let require = true;
     * let limit = true;
     *
     *  setDisplay(display)
     *  setRequired(required)
     *  setLimit(limit)
     *  let conditionalRules = [ {type: 'DISPLAY_CONDITION', expression: 'fld_1010 == 60', result: undefined} ]
     * evaluateCondition(conditionalRules) => {
     *  conditionalRules.map(rule => {
     *    rule.expression = normalizeString(rule.expression);
     *  })
     *    if()
     *    let returnResult = {"display": true, "required": false, "limit": true}
     *    return returnResult;
     * }
     *
     */

    conditionnalQuestion.conditionalRules.map((rule, id) => {
      // TODO: Think about normalize string method to run only once
      rule.normalizeExpression = normalizeString(rule.expression);
    });

    // Normalizing is done just above this comment
    // const smartCondition: string = doCompulsoryCondition
    //   ? normalizeString(conditionnalQuestion.compulsoryCondition)
    //   : doLimitCondition
    //   ? normalizeString(conditionnalQuestion.limitCondition.condition)
    //   : normalizeString(conditionnalQuestion.condition);

    // Creating all key value pairs inside obj
    const obj = Object.entries(object)
      .map(([key, value]) => {
        // If target value is type of array take value from 0 index
        const targetValue = Array.isArray(value) ? value[0] : value;
        return `const ${key}=${getValueString(targetValue)};`;
      })
      .join('\n');

    function transformDate(dateString: string) {
      // Extract date part only (remove time part if present)
      // Handles formats like: dd/mm/yyyy HH:mm, dd/mm/yyyy hh:mm a, etc.
      const dateOnly = dateString.split(/\s+/)[0];

      const splitDate = dateOnly.includes('/')
        ? dateOnly.split('/')
        : dateOnly.split('-');

      const needParser = splitDate[2]?.length > 2;
      let parsedDate = '';
      if (needParser) {
        parsedDate = `${splitDate[2]}/${splitDate[1]}/${splitDate[0]}`;
      } else {
        parsedDate = dateOnly;
      }

      // If original string had time, append it back for proper parsing
      const timePart = dateString.substring(dateOnly.length).trim();
      if (timePart) {
        // Parse the full date-time string
        return `${parsedDate} ${timePart}`;
      }

      return parsedDate;
    }

    function getValueString(value) {
      if (
        value === undefined ||
        (value && value.length === 0) ||
        value === ''
      ) {
        return 'undefined';
      }
      if (value === null) {
        return 'null';
      }

      // If value contains new line specially in TextArea, need to replace by space
      const pattern = /\s?\r?\n|\r\s?/g;
      const newLine = value ? pattern.exec(value) : false;
      if (newLine) {
        return `'${value.replace(pattern, ' ')}'`;
      }

      // If value contains date, need to construct value as a date
      const datePattern = /\/|-/g;
      const existDate = value ? datePattern.exec(value) : false;
      if (existDate) {
        // Parse the date string value received from the form input
        let parsedDateString = transformDate(value);
        const parsedDate = new Date(parsedDateString);
        return parsedDate.getTime();
      }

      // If value contains number or float value return as a number else return string
      const parsedNumber = Number.parseFloat(value);
      return Number.isNaN(parsedNumber) ? `'${value}'` : parsedNumber;
    }

    function findAllMatches(exp: RegExp, str: string) {
      let lastResult = null;
      const results = [];
      do {
        lastResult = exp.exec(str);
        if (lastResult) {
          results.push(lastResult);
        }
      } while (lastResult);
      return results.map(r => ({ index: r.index, data: r[0] }));
    }

    function normalizeString(baseStr) {
      if (baseStr) {
        let str = baseStr
          .replace(/\s?=+\s?'/g, ` == '`)
          .replace(/\s?>\s?=+\s?/g, ' >= ')
          .replace(/\s?<\s?=+\s?/g, ' <= ')
          .replace(/\s?=+\s?''/g, ' == undefined')
          .replace(/\s?!\s?=+/g, ' != ')
          .replace(
            /((?!(\"|\')[\w\s]*[(\\"|\\')]*[\w\s]*)\s(and)\s(?![\w\s]*[(\\"|\\')]*[\w\s]*(\"|')))/gis,
            ' && '
          )
          .replace(
            /((?!(\"|\')[\w\s]*[(\\"|\\')]*[\w\s]*)\s(or)\s(?![\w\s]*[(\\"|\\')]*[\w\s]*(\"|')))/gis,
            ' || '
          );

        // If expression contains date or date-time to evaluate
        // Matches: dd/mm/yyyy, yyyy/mm/dd, dd/mm/yyyy HH:mm, dd/mm/yyyy hh:mm a, etc.
        const matchesDate = str.match(
          /\d{2}\-?\/?\d{2}\-?\/?\d{4}(\s+\d{1,2}:\d{2}(:\d{2})?(\s*[aApP][mM])?)?|\d{4}\-?\/?\d{2}\-?\/?\d{2}(\s+\d{1,2}:\d{2}(:\d{2})?(\s*[aApP][mM])?)?/g
        );
        if (matchesDate) {
          matchesDate.forEach(date => {
            let parsedDateString = transformDate(date);
            const getTime = new Date(parsedDateString).getTime();
            str = str.replace(date, getTime);
          });
        }

        const matches = findAllMatches(/('[0-9\.]*')/g, str);
        let outputStr = '';
        let lastMatchEndIndex = 0;
        for (let i = 0; i < matches.length; ++i) {
          const { index, data } = matches[i];
          const stringWithoutQuotes = data.substring(1, data.length - 1);
          const parsedStr = Number.parseFloat(stringWithoutQuotes);
          outputStr += str.substring(lastMatchEndIndex, index);
          outputStr += parsedStr;
          lastMatchEndIndex = index + data.length;
        }
        outputStr += str.substr(lastMatchEndIndex);
        return outputStr;
      }
      return null;
    }

    conditionnalQuestion.conditionalRules.map((rule, id) => {
      let res = false;
      try {
        res = Function(obj + 'return ' + rule.normalizeExpression)();
      } catch (e) {
        res = undefined;
        // console.log(e);
      }
      rule.result = res;
    });
    // decision = Function(obj + 'return ' + smartCondition)();
    return conditionnalQuestion.conditionalRules;
  }

  // private areParentQuestionsValid(
  //   conditionnalQuestion: QuestionsV3,
  //   formValues: any
  // ): boolean {
  //   const fields = conditionnalQuestion.conditionalRules.parentQuestions;
  //   let validFields = 0;
  //   const numberOfFieldsToValid =
  //     conditionnalQuestion.conditionalRules.parentQuestions.length;

  //   fields.forEach(field => {
  //     let validTrigger = 0;
  //     const numberOfTriggerToValid = field.hasMultipleValuesToSatisfy
  //       ? field.valuesToBeTruthy.length
  //       : 1;
  //     if (Array.isArray(formValues[field.name])) {
  //       field.valuesToBeTruthy.forEach(value => {
  //         if (
  //           formValues[field.name].includes(value) &&
  //           validTrigger < numberOfTriggerToValid
  //         ) {
  //           validTrigger++;
  //         }
  //       });

  //       if (validTrigger === numberOfTriggerToValid) {
  //         validFields++;
  //       }
  //     } else {
  //       if (
  //         formValues[field.name] === '' &&
  //         conditionnalQuestion.conditionalRules.logicOperator === 'all'
  //       ) {
  //         return false;
  //       }
  //       switch (field.comparisonOperator) {
  //         case NumericComparison.eq:
  //           validFields = isNaN(+field.valuesToBeTruthy[0])
  //             ? field.valuesToBeTruthy[0] === formValues[field.name]
  //               ? 1
  //               : 0
  //             : +field.valuesToBeTruthy[0] === +formValues[field.name]
  //             ? 1
  //             : 0;
  //           break;

  //         case NumericComparison.neq:
  //           validFields =
  //             +field.valuesToBeTruthy[0] !== +formValues[field.name] ? 1 : 0;
  //           break;

  //         case NumericComparison.lt:
  //           validFields =
  //             +formValues[field.name] < +field.valuesToBeTruthy[0] ? 1 : 0;
  //           break;

  //         case NumericComparison.lte:
  //           validFields =
  //             +formValues[field.name] <= +field.valuesToBeTruthy[0] ? 1 : 0;
  //           break;

  //         case NumericComparison.gt:
  //           validFields =
  //             +formValues[field.name] > +field.valuesToBeTruthy[0] ? 1 : 0;
  //           break;

  //         case NumericComparison.gte:
  //           validFields =
  //             +formValues[field.name] >= +field.valuesToBeTruthy[0] ? 1 : 0;
  //           break;

  //         default:
  //           if (field.valuesToBeTruthy.includes(formValues[field.name])) {
  //             validFields++;
  //           }
  //           break;
  //       }
  //     }
  //   });
  //   return conditionnalQuestion.conditionalRules.logicOperator === 'all'
  //     ? numberOfFieldsToValid === validFields
  //     : validFields >= 1;
  // }

  // private isParentQuestionValid(
  //   conditionnalQuestion: QuestionsV3,
  //   formValues: any
  // ): boolean {
  //   return conditionnalQuestion.conditionalRules.parentQuestions.reduce(
  //     (acc, field) => {
  //       if (acc) {
  //         return acc;
  //       }

  //       if (Array.isArray(formValues[field.name])) {
  //         return formValues[field.name].reduce((bool, value) => {
  //           return bool || field.valuesToBeTruthy.includes(value);
  //         }, false);
  //       } else {
  //         field.valuesToBeTruthy.includes(formValues[field.name]);
  //       }
  //     },
  //     false
  //   );
  // }

  private parseArrayOfBooleanInFormValues(formValues: any): {
    [key: string]: string;
  } {
    /*
    ! the goal is to transform an array of Boolean
    ! to an array of string matching de position of true
    ! in the parsed array

    example:
      {
       key1: [false, true, true],
       key2: [false, false, false],
       key3: [true, false, false],
      }
    is transform to
      {
        key1: [ "1", "2"],
        key2: [],
        key3: ["0"],
      }
    */

    return Object.keys(formValues).reduce((acc, key) => {
      if (Array.isArray(formValues[key])) {
        return {
          ...acc,
          [key]: formValues[key]
            .map((f, index) => (f === true ? index.toString() : f))
            .filter(f => !!f)
        };
      }
      return { ...acc, [key]: formValues[key] };
    }, {});
  }

  /**
   * Form V3 Add conditional rules for questions
   * * 1. Check if question is conditional or not
   * * 2. If it is conditional, it receives 'display || compulsory || limit' conditions from api
   * * 3. If it has any conditional case matched with ConditionalRuleTypes
   * @returns conditionalRules
   * */

  private parseConditionalRules(question: any): Array<ConditionalRuleType> {
    let conditionalRules: Array<ConditionalRuleType> = [];
    Object.keys(ConditionalRuleTypes).map((key, id) => {
      if (question[ConditionalRuleTypes[key]]) {
        conditionalRules.push({
          type: key,
          expression: question[ConditionalRuleTypes[key]]
        });
      }
    });
    return conditionalRules;
  }

  // Add Conditional Rules
  // private addConditionalRulesToQuestion(
  //   question
  // ): DynamicFormInputQuestionV3['conditionalRules'] {
  //   const condictionDict = {
  //     equals: NumericComparison.eq,
  //     diff: NumericComparison.neq,
  //     inf: NumericComparison.lt,
  //     infequals: NumericComparison.lte,
  //     sup: NumericComparison.gt,
  //     supequals: NumericComparison.gte
  //   };

  //   let rules: DynamicFormInputQuestionV3['conditionalRules'] = {
  //     isConditionnal: false,
  //     logicOperator: ''
  //   };

  //   if (
  //     question &&
  //     question.logic &&
  //     question.logic.checks &&
  //     question.logic.checks.length > 0
  //   ) {
  //     const parentQuestions = question.logic.checks.map(check => ({
  //       name: check['field'],
  //       valuesToBeTruthy: Array.isArray(check.option)
  //         ? [...check.option]
  //         : [check.option.toString()],
  //       hasMultipleValuesToSatisfy: check.condition === 'all',
  //       comparisonOperator: condictionDict[check.condition]
  //     }));
  //     rules = {
  //       ...rules,
  //       isConditionnal: true,
  //       hasMultipleParentQuestions:
  //         question.logic.conditional === 'all' ||
  //         question.logic.conditional === 'any',
  //       logicOperator: question.logic.conditional,
  //       parentQuestions
  //     };
  //   }

  //   return rules;
  // }

  private transformOptionsObjectToArray(optionsObject: any): any[] {
    return Object.keys(optionsObject)
      .map(option => optionsObject[option])
      .map(option => {
        const { value: name, label: value } = option;
        return { value, name };
      });
  }

  private addCustomCSSClasses(question, responses: any[]): string[] {
    const toBeCheckedClassname = 'dynamic-form__to-be-checked';
    const requiredClassname = 'dynamic-form__required';
    const classes = [];
    const response = responses.find(r => r.uuid === question['name']);

    if (response && (response.to_be_checked || response.toBeChecked)) {
      classes.push(toBeCheckedClassname);
    }

    if (parseInt(question.required, 10) === 1) {
      classes.push(requiredClassname);
    }

    return classes;
  }

  private parseType(type: string, question: any) {
    switch (type.toLowerCase()) {
      case 'eq5':
        return 'eq5';

      case 'eq5-validated':
        return 'eq5-validated';

      case 'richtext':
        return 'richtext';

      case 'integer':
        return question.use_slider === 1 ? 'numberRange' : 'number';

      case 'select':
        if (question.multiple === '1') {
          return 'multiDropdown';
        } else {
          return type;
        }

      case 'time':
        return 'time';

      case 'date':
        return 'date';

      case 'datetime':
        return 'datetime';

      case 'boolean':
        return 'radio';

      case 'image_file':
        return 'photo';

      default:
        return type;
    }
  }
}
